import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { getService } from "@/api";
import { useSeo, faqSchema } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";
import { CalendlyCta } from "@/components/SiteChrome";

const SERVICES = [
  { title: "Family Office Setup", body: "Establish a single or multi-family office in DIFC, ADGM or DMCC, jurisdiction selection, entity formation, banking and operations, end to end." },
  { title: "Wealth Structuring & Foundations", body: "DIFC and ADGM foundations, trusts, holding companies and SPVs that separate family wealth from business risk." },
  { title: "Succession & Estate Planning", body: "DIFC and Abu Dhabi wills, succession frameworks and orderly transfer of control to the next generation." },
  { title: "Family Governance", body: "Family constitutions, councils and decision rules that keep the family aligned as the wealth grows." },
  { title: "Investment Oversight & Reporting", body: "One consolidated view of every asset, in every jurisdiction, reporting, manager selection and risk monitoring." },
  { title: "Tax, Compliance & Administration", body: "UAE Corporate Tax, AML and substance, plus Indian FEMA, LRS and ODI compliance and reporting." },
  { title: "Residency, Relocation & Lifestyle", body: "Golden Visa, relocation, schooling, property and concierge support for the whole family." },
];

const SFO_MFO = {
  sfo: {
    title: "Single Family Office",
    points: ["Serves one family only", "Full control and privacy", "Own team and cost base", "Suits larger, more complex estates"],
  },
  mfo: {
    title: "Multi Family Office",
    points: ["Serves several families", "Shares costs between them", "Needs a financial services licence in DIFC/ADGM if advising", "Suits families building toward an SFO"],
  },
};

const JURISDICTIONS = [
  { name: "DIFC", legal: "English-based common law, DIFC Courts", wealth: "US$50m aggregate net assets", license: "Not required for a pure SFO", best: "Larger families wanting Dubai's courts and ecosystem" },
  { name: "ADGM", legal: "English common law applied directly, ADGM Courts", wealth: "US$10m family net assets", license: "Not required for a pure SFO", best: "Families from US$10m wanting a common-law home at lower cost" },
  { name: "DMCC", legal: "UAE law, free zone rules", wealth: "US$1m liquid or investible assets", license: "Single Family Office licence", best: "Smaller, family-only offices starting out" },
];

const PROCESS = [
  { step: "Discovery", body: "We meet the family, map assets across every jurisdiction, and understand goals and the people involved." },
  { step: "Structuring proposal", body: "We recommend the jurisdiction, entities, governance and residency plan, with costs and a timeline." },
  { step: "Establishment", body: "We form entities, prepare applications, open bank accounts, register wills and apply for visas." },
  { step: "Governance in place", body: "We draft the family constitution, set up councils and agree the investment policy." },
  { step: "Ongoing operation & review", body: "We run administration, compliance and reporting, with a quarterly review and an annual family meeting." },
];

const FAQS = [
  { q: "What is a family office?", a: "A private structure that owns, manages and protects a family's wealth, combining investment oversight, administration, governance and succession planning in one place." },
  { q: "Single or multi-family office?", a: "A single family office serves one family with full control and privacy. A multi-family office serves several families and shares costs; in DIFC and ADGM it needs a financial services licence if it advises on or manages investments." },
  { q: "How much wealth do I need for a UAE family office?", a: "Roughly US$50m in family net assets for DIFC, US$10m for ADGM and US$1m in liquid assets for DMCC. Families below these levels can use a foundation or an outsourced family office." },
  { q: "Is a family office tax-free in the UAE?", a: "Not automatically. The UAE has no personal income tax, but a family office company is generally subject to 9% Corporate Tax unless it qualifies for the 0% Free Zone rate. A family foundation meeting Article 17 conditions can apply to be tax transparent." },
  { q: "Can Indian residents set up a UAE family office?", a: "Yes, through LRS for individuals or ODI for companies and LLPs, provided FEMA rules, reporting and Indian tax obligations are met. This is where our India experience matters most." },
];

export default function ServiceFamilyOffice() {
  const [service, setService] = useState(null);
  useSeo({
    title: "Family Office Setup Dubai, DIFC, ADGM & DMCC, TruVerse",
    description:
      "Family office setup, wealth structuring and succession planning in the UAE, with deep India expertise. DIFC, ADGM and DMCC. Book a private consultation.",
    schema: faqSchema(FAQS),
  });
  useEffect(() => {
    getService("family-office").then(setService).catch(() => setService({}));
  }, []);

  if (!service) return <div className="container-site py-32 text-muted-foreground" data-testid="service-fo-loading">Loading…</div>;

  return (
    <div data-testid="service-family-office-page">
      <section className="bg-ink py-24 lg:py-32">
        <div className="container-site max-w-4xl">
          <Reveal>
            <h1 className="mt-2 text-4xl font-bold leading-tight tracking-tight text-ink-fg md:text-6xl">
              A durable home for your family's wealth, built across India and the Gulf.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              Years of running family offices in India, brought to families formalising their wealth in the UAE, from
              structure and succession to governance and day-to-day administration.
            </p>
            <div className="mt-10">
              <CalendlyCta label="Book a private consultation" testId="fo-cta-book-btn" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24" data-testid="fo-services-section">
        <div className="container-site grid gap-12 lg:grid-cols-3">
          <Reveal>
            <SectionHead eyebrow="What we do" title="One partner for the whole of your family's wealth." intro="The structure that holds it, the people who govern it, and the administration that keeps it compliant." />
            <div className="mt-10 border border-border bg-card p-8" data-testid="fo-confidentiality-box">
              <p className="eyebrow-text">Confidentiality</p>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                Discretion governs how we hold information, staff engagements and communicate. Structures, names and holdings are never discussed outside the engagement.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <Accordion type="single" collapsible className="w-full" data-testid="fo-services-accordion">
              {SERVICES.map((s, i) => (
                <AccordionItem key={s.title} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold text-foreground" data-testid={`fo-accordion-trigger-${i}`}>
                    {s.title}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{s.body}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary/60 py-24" data-testid="fo-sfo-mfo-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="First decision" title="Single or multi family office?" />
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {[SFO_MFO.sfo, SFO_MFO.mfo].map((col, i) => (
              <Reveal key={col.title} delay={i * 0.1}>
                <div className="h-full border border-border bg-card p-8" data-testid={`fo-${i === 0 ? "sfo" : "mfo"}-card`}>
                  <h3 className="text-2xl font-bold text-foreground">{col.title}</h3>
                  <ul className="mt-6 space-y-3">
                    {col.points.map((p) => (
                      <li key={p} className="flex items-start gap-4 text-sm text-muted-foreground">
                        <span className="mt-2 h-px w-5 shrink-0 bg-accent" aria-hidden="true" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="fo-jurisdiction-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="Where to establish" title="DIFC, ADGM or DMCC?" intro="The wealth threshold alone often decides the shortlist; legal system, cost and the family's location decide the rest." />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 overflow-x-auto border border-border bg-card" data-testid="fo-jurisdiction-table">
              <table className="w-full min-w-[820px] text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-xs uppercase tracking-[0.15em] text-muted-foreground">
                    <th className="p-5 font-semibold">Jurisdiction</th>
                    <th className="p-5 font-semibold">Legal system</th>
                    <th className="p-5 font-semibold">Minimum family wealth</th>
                    <th className="p-5 font-semibold">Licence for a pure SFO</th>
                    <th className="p-5 font-semibold">Best suited to</th>
                  </tr>
                </thead>
                <tbody>
                  {JURISDICTIONS.map((j) => (
                    <tr key={j.name} className="border-b border-border last:border-0">
                      <td className="p-5 text-lg font-bold text-foreground">{j.name}</td>
                      <td className="p-5 text-muted-foreground">{j.legal}</td>
                      <td className="p-5 text-muted-foreground">{j.wealth}</td>
                      <td className="p-5 text-muted-foreground">{j.license}</td>
                      <td className="p-5 text-muted-foreground">{j.best}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-[11px] italic text-muted-foreground" data-testid="fo-jurisdiction-note">
              Indicative figures as reported by DIFC, ADGM and DMCC, confirm before relying on them. Includes DIFC Family Arrangements Regulations (2023).
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-secondary/60 py-24" data-testid="fo-process-section">
        <div className="container-site">
          <Reveal>
            <SectionHead eyebrow="How we work" title="A clear path from first conversation to a working family office." />
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.08}>
                <div className="border-t-2 border-accent pt-6" data-testid={`fo-process-step-${i}`}>
                  <span className="text-3xl font-bold text-accent/30">{i + 1}</span>
                  <p className="mt-2 text-base font-bold text-foreground">{p.step}</p>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="fo-faq-section">
        <div className="container-site max-w-3xl">
          <Reveal>
            <SectionHead eyebrow="FAQs" title="What families ask first." />
            <Accordion type="single" collapsible className="mt-10 w-full" data-testid="fo-faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-medium text-foreground" data-testid={`fo-faq-trigger-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-20" data-testid="fo-final-cta">
        <div className="container-site flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <h2 className="max-w-xl text-3xl font-bold tracking-tight text-ink-fg md:text-4xl">Request a confidential conversation.</h2>
            <p className="mt-4 max-w-xl text-xs leading-relaxed text-ink-muted" data-testid="fo-compliance-note">
              TruVerse provides advisory and setup services; regulated activities are carried out with licensed partners where required.
            </p>
          </div>
          <CalendlyCta label="Book a private consultation" testId="fo-bottom-book-btn" />
        </div>
      </section>
    </div>
  );
}
